import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: "website" | "product" | "article";
  noindex?: boolean;
  schema?: object | object[];
  keywords?: string;
}

export const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = "https://vapebolag.se/og-image.jpg",
  ogType = "website",
  noindex = false,
  schema,
  keywords,
}: SEOHeadProps) => {
  const fullTitle = title.includes("Vapebolag") ? title : `${title} | Vapebolag`;
  const baseUrl = "https://vapebolag.se";
  const canonicalUrl = canonical ? `${baseUrl}${canonical}` : undefined;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Hreflang - self-referencing Swedish */}
      {canonicalUrl && <link rel="alternate" hrefLang="sv-SE" href={canonicalUrl} />}
      {canonicalUrl && <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content="sv_SE" />
      <meta property="og:site_name" content="Vapebolag - THCA Vapes Sverige" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(schema) ? schema : [schema])}
        </script>
      )}
    </Helmet>
  );
};

// Helper to create Product schema (Google Rich Results compliant)
// Required fields: name, offers (with price, priceCurrency, availability)
// Optional: aggregateRating (only if real reviews exist)
export const createProductSchema = (product: {
  title: string;
  description: string;
  handle: string;
  price: number;
  compareAtPrice?: number;
  currency: string;
  imageUrl?: string;
  vendor?: string;
  sku?: string;
  inStock?: boolean;
  gtin?: string;
  mpn?: string;
  aggregateRating?: {
    ratingValue: number;
    reviewCount: number;
  };
}) => {
  const baseSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `https://vapebolag.se/product/${product.handle}#product`,
    name: product.title,
    description: product.description || `Premium ${product.title} från Vapebolag - Labbtestad kvalitet, snabb leverans.`,
    url: `https://vapebolag.se/product/${product.handle}`,
    image: product.imageUrl || "https://vapebolag.se/og-image.jpg",
    sku: product.sku || product.handle,
    mpn: product.mpn || product.handle,
    brand: {
      "@type": "Brand",
      name: product.vendor || "Vapebolag",
    },
    offers: {
      "@type": "Offer",
      "@id": `https://vapebolag.se/product/${product.handle}#offer`,
      url: `https://vapebolag.se/product/${product.handle}`,
      priceCurrency: product.currency || "SEK",
      price: product.price.toFixed(2),
      priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      availability: product.inStock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: {
        "@type": "Organization",
        name: "Vapebolag",
        url: "https://vapebolag.se",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: "SE",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: 0,
            maxValue: 1,
            unitCode: "DAY",
          },
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: 1,
            maxValue: 3,
            unitCode: "DAY",
          },
        },
      },
    },
  };

  // Add aggregateRating only when real reviews exist
  if (product.aggregateRating && product.aggregateRating.reviewCount > 0) {
    baseSchema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: product.aggregateRating.ratingValue,
      bestRating: 5,
      worstRating: 1,
      reviewCount: product.aggregateRating.reviewCount,
    };
  }

  return baseSchema;
};

// Helper to create CollectionPage schema
export const createCollectionSchema = (collection: {
  name: string;
  description: string;
  slug: string;
  productCount: number;
}) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "@id": `https://vapebolag.se/kollektioner/${collection.slug}#collection`,
  name: collection.name,
  description: collection.description,
  url: `https://vapebolag.se/kollektioner/${collection.slug}`,
  mainEntity: {
    "@type": "ItemList",
    numberOfItems: collection.productCount,
    itemListElement: [],
  },
  breadcrumb: {
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Hem",
        item: "https://vapebolag.se",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: collection.name,
        item: `https://vapebolag.se/kollektioner/${collection.slug}`,
      },
    ],
  },
});

// Helper to create BreadcrumbList schema
export const createBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: `https://vapebolag.se${item.url}`,
  })),
});

// Helper to create FAQPage schema for product pages
export const createFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

// Helper to create Article schema for blog posts
export const createArticleSchema = (article: {
  title: string;
  description: string;
  slug: string;
  datePublished: string;
  dateModified?: string;
  author: string;
  imageUrl?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "@id": `https://vapebolag.se/blogg/${article.slug}#article`,
  headline: article.title,
  description: article.description,
  url: `https://vapebolag.se/blogg/${article.slug}`,
  image: article.imageUrl || "https://vapebolag.se/og-image.jpg",
  datePublished: article.datePublished,
  dateModified: article.dateModified || article.datePublished,
  author: {
    "@type": "Organization",
    name: article.author,
    url: "https://vapebolag.se",
  },
  publisher: {
    "@type": "Organization",
    name: "Vapebolag",
    logo: {
      "@type": "ImageObject",
      url: "https://vapebolag.se/vapebolag-logo.webp",
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `https://vapebolag.se/blogg/${article.slug}`,
  },
  inLanguage: "sv-SE",
});

// Homepage Organization + WebSite schema
export const createHomepageSchema = () => [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://vapebolag.se/#organization",
    name: "Vapebolag",
    url: "https://vapebolag.se",
    logo: {
      "@type": "ImageObject",
      url: "https://vapebolag.se/vapebolag-logo.webp",
      width: 512,
      height: 512,
    },
    sameAs: [],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      availableLanguage: ["Swedish"],
      email: "info@vapebolag.se",
    },
    address: {
      "@type": "PostalAddress",
      addressCountry: "SE",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://vapebolag.se/#website",
    name: "Vapebolag",
    url: "https://vapebolag.se",
    publisher: {
      "@id": "https://vapebolag.se/#organization",
    },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: "https://vapebolag.se/search?q={search_term_string}",
      },
      "query-input": "required name=search_term_string",
    },
    inLanguage: "sv-SE",
  },
  {
    "@context": "https://schema.org",
    "@type": "Store",
    "@id": "https://vapebolag.se/#store",
    name: "Vapebolag",
    url: "https://vapebolag.se",
    description: "Sveriges ledande butik för THCA vapes och hash. Labbtestad kvalitet, 100% lagligt.",
    priceRange: "$$",
    currenciesAccepted: "SEK",
    paymentAccepted: "Credit Card, Debit Card, Swish",
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "00:00",
      closes: "23:59",
    },
    address: {
      "@type": "PostalAddress",
      addressCountry: "SE",
    },
  },
];
